import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabaseClient'
import type { EventRow } from '@/lib/types'
import { PageSpinner, EmptyState } from '@/components/ui'

interface EventContextValue {
  event: EventRow
  reload: () => Promise<void>
}

const EventContext = createContext<EventContextValue | null>(null)

/** Wraps every /admin/events/:eventId/* route: loads the event once and makes it available to nested pages without each one re-fetching it. */
export function EventProvider({ children }: { children: React.ReactNode }) {
  const { eventId } = useParams<{ eventId: string }>()
  const [event, setEvent] = useState<EventRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const load = useCallback(async () => {
    if (!eventId) return
    setLoading(true)
    const { data, error } = await supabase.from('events').select('*').eq('id', eventId).maybeSingle()
    if (error || !data) {
      setNotFound(true)
    } else {
      setEvent(data as EventRow)
    }
    setLoading(false)
  }, [eventId])

  useEffect(() => {
    load()
  }, [load])

  if (loading) return <PageSpinner label="Loading event…" />
  if (notFound || !event) return <EmptyState title="Event not found" description="It may have been archived or the link is incorrect." />

  return <EventContext.Provider value={{ event, reload: load }}>{children}</EventContext.Provider>
}

export function useEvent() {
  const ctx = useContext(EventContext)
  if (!ctx) throw new Error('useEvent must be used within EventProvider')
  return ctx
}
